import React, { useEffect, useRef } from 'react';
import useOtpLogin from '../../hooks/useOtpLogin';

/**
 * OtpLoginCard — email OTP login for the landing page (CRT terminal look).
 *
 * Flow: email → 6-digit code (auto-submits when complete) → kuutio_session
 * cookie → /instances. Resend has a 30 s cooldown; the email can be changed
 * without restarting the page. An expired code points straight at resend.
 */

const CYAN = '#5ff7ff';
const GLOW = '0 0 6px rgba(95, 247, 255, 0.55)';
const FONT = "'VT323', 'IBM Plex Mono', 'Courier New', ui-monospace, monospace";
const RED = '#ff7b7b';
const GREEN = '#7bffb2';

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 18, minHeight: 150 },
  label: { color: CYAN, fontFamily: FONT, fontSize: 14, opacity: 0.8, letterSpacing: '0.05em' },
  input: {
    padding: '12px 16px', background: 'rgba(0,0,0,0.5)', color: CYAN, fontFamily: FONT,
    fontSize: 17, letterSpacing: '0.06em', border: '1px solid rgba(95,247,255,0.45)',
    borderRadius: 2, outline: 'none', width: 260, textAlign: 'center', textShadow: GLOW,
  },
  codeInput: { fontSize: 30, letterSpacing: '0.45em', width: 200, paddingRight: 2 },
  button: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '10px 22px',
    background: 'transparent', color: CYAN, fontFamily: FONT, fontSize: 16, letterSpacing: '0.08em',
    border: `1px solid ${CYAN}`, borderRadius: 2, cursor: 'pointer', textShadow: GLOW,
    transition: 'background 0.15s, box-shadow 0.15s',
  },
  link: {
    background: 'none', border: 'none', color: CYAN, fontFamily: FONT, fontSize: 14,
    opacity: 0.7, cursor: 'pointer', textDecoration: 'underline', padding: 2,
  },
  note: { fontFamily: FONT, fontSize: 14, letterSpacing: '0.04em' },
};

export default function OtpLoginCard() {
  const otp = useOtpLogin({ initialStep: 'closed', onSuccess: () => window.location.assign('/instances') });
  const { step, setStep, email, setEmail, code, setCode, busy, error, notice, cooldown, expired, requestCode, verifyCode, changeEmail } = otp;
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, [step]);

  if (step === 'closed') {
    return (
      <button style={{ ...styles.link, marginTop: 16, fontSize: 15 }} onClick={() => setStep('email')}>
        &gt; Kirjaudu sähköpostikoodilla
      </button>
    );
  }

  if (step === 'email') {
    return (
      <div style={styles.wrap}>
        <div style={styles.label}>Sähköpostiosoitteesi:</div>
        <input
          ref={inputRef}
          style={styles.input}
          type="email"
          placeholder="etunimi.sukunimi@sitra.fi"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && requestCode()}
          disabled={busy}
        />
        <button style={{ ...styles.button, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => requestCode()}>
          {busy ? 'Lähetetään...' : '> Lähetä koodi'}
        </button>
        {error && <div style={{ ...styles.note, color: RED }}>{error}</div>}
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.label}>
        Koodi lähetetty: <strong>{email}</strong>{' '}
        <button style={styles.link} onClick={changeEmail}>
          vaihda
        </button>
      </div>
      <input
        ref={inputRef}
        style={{ ...styles.input, ...styles.codeInput }}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="••••••"
        value={code}
        onChange={e => setCode(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && code.length === 6 && verifyCode(code)}
        disabled={busy}
      />
      <button
        style={{ ...styles.button, opacity: busy || code.length !== 6 ? 0.5 : 1 }}
        disabled={busy || code.length !== 6}
        onClick={() => verifyCode(code)}
      >
        {busy ? '...' : '> Kirjaudu'}
      </button>
      <button
        style={{ ...styles.link, ...(expired ? { opacity: 1, color: GREEN, fontSize: 15 } : {}) }}
        disabled={busy || cooldown > 0}
        onClick={() => requestCode(true)}
      >
        {cooldown > 0 ? `Lähetä uusi koodi (${cooldown}s)` : 'Lähetä uusi koodi'}
      </button>
      {error && <div style={{ ...styles.note, color: RED }}>{error}</div>}
      {notice && <div style={{ ...styles.note, color: GREEN }}>{notice}</div>}
    </div>
  );
}
